import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { Button } from '../../components/ui';

/** The warning comes this long before the form closes (WCAG 2.2.1: at least 20 s). */
export const WARN_BEFORE_MS = 2 * 60 * 1000;

/**
 * When the form closes itself for privacy (SFM-D10): the session's idle limit
 * or its four-hour limit, whichever comes first. Two minutes before, `warning`
 * turns true; at the limit, `onExpire` runs once.
 *
 * Driven by the server's own times, so a laptop left asleep does not resume
 * into a session the server has already ended.
 */
export function usePrivacyTimeout(
  limits: { idleUntil: string; until: string | null } | null,
  onExpire: () => void,
): { warning: boolean } {
  const [warning, setWarning] = useState(false);
  const expire = useRef(onExpire);
  expire.current = onExpire;

  useEffect(() => {
    setWarning(false);
    if (!limits) return;
    const end = Math.min(
      Date.parse(limits.idleUntil),
      limits.until ? Date.parse(limits.until) : Number.POSITIVE_INFINITY,
    );
    if (!Number.isFinite(end)) return;
    const now = Date.now();
    const warnIn = Math.max(end - WARN_BEFORE_MS - now, 0);
    const warnTimer = setTimeout(() => setWarning(true), warnIn);
    const endTimer = setTimeout(() => expire.current(), Math.max(end - now, 0));
    return () => {
      clearTimeout(warnTimer);
      clearTimeout(endTimer);
    };
  }, [limits?.idleUntil, limits?.until]);

  return { warning };
}

/**
 * "Are you still there?" — an alert dialog, so it is announced wherever the
 * patient is, with "Keep going" focused: one tap gives them another twenty
 * minutes (WCAG 2.2.1 Timing Adjustable).
 */
export function TimeoutDialog({
  onKeepGoing,
  onClose,
  busy,
}: {
  onKeepGoing: () => void;
  onClose: () => void;
  busy: boolean;
}) {
  const { t } = useI18n();
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // "Keep going" is the first button in the dialog.
    dialog.current?.querySelector('button')?.focus();
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/40 p-4 sm:items-center">
      <div
        ref={dialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="privacy-timeout-heading"
        aria-describedby="privacy-timeout-body"
        className="w-full max-w-md space-y-4 rounded-2xl border border-black/5 bg-card p-6 shadow-panel"
        onKeyDown={(event) => {
          if (event.key === 'Escape') onKeepGoing();
        }}
      >
        <h2 id="privacy-timeout-heading" className="text-xl font-semibold text-heading">
          {t('form.timeoutHeading')}
        </h2>
        <p id="privacy-timeout-body" className="text-base text-foreground">
          {t('form.timeoutBody')}
        </p>
        <div className="flex flex-col gap-3 sm:flex-row-reverse">
          <Button loading={busy} onClick={onKeepGoing}>
            {t('form.keepGoing')}
          </Button>
          <Button variant="outline" onClick={onClose}>
            {t('form.closeNow')}
          </Button>
        </div>
      </div>
    </div>
  );
}
